import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { useShop } from "../context/ShopContext";
import {
  FaFileAlt, FaExchangeAlt, FaRupeeSign, FaChartBar,
  FaPrint, FaShoppingCart, FaHome, FaChevronDown,
  FaSignOutAlt, FaTimes,
} from "react-icons/fa";

/* ─── theme tokens ───────────────────────────────────── */
const BG        = "#1a2035";
const BG2       = "rgba(0,0,0,0.2)";
const ACTIVE_BG = "rgba(99,102,241,0.18)";
const HOVER_BG  = "rgba(255,255,255,0.05)";
const TEXT      = "#CBD5E1";
const MUTED     = "#64748B";
const INDIGO    = "#6366F1";

/* ─── menu config ────────────────────────────────────── */
const SECTIONS = [
  {
    perm: "पर्चा",
    label: "पर्चा", sub: "Sales",
    Icon: FaFileAlt, color: "#3B82F6",
    items: [
      { label: "देशी पर्चा",           sub: "Desi Invoice",       path: "/desistock" },
      { label: "अंग्रेजी पर्चा",        sub: "English Invoice",    path: "/stockmanagement" },
      { label: "पिछला देशी पर्चा",      sub: "Previous Desi",      path: "/desistockentry" },
      { label: "पिछला अंग्रेजी पर्चा",  sub: "Previous English",   path: "/stock" },
    ],
  },
  {
    perm: "आमद भेजान",
    label: "आमद भेजान", sub: "Stock Movement",
    Icon: FaExchangeAlt, color: "#10B981",
    items: [
      { label: "देशी आमद",              sub: "Desi Inward",        path: "/editdesistockwithdate" },
      { label: "अंग्रेजी आमद",           sub: "English Inward",     path: "/editstockwithdate" },
      { label: "बॉक्स देशी आमद",        sub: "Box Desi In",        path: "/boxdesi" },
      { label: "बॉक्स अंग्रेजी आमद",    sub: "Box English In",     path: "/boxenglish" },
      { label: "देशी भेजान",            sub: "Desi Outward",       path: "/editdesitransferstock" },
      { label: "अंग्रेजी भेजान",         sub: "English Outward",    path: "/edittransferstock" },
      { label: "बॉक्स देशी भेजान",      sub: "Box Desi Out",       path: "/boxdesitransfer" },
      { label: "बॉक्स अंग्रेजी भेजान",  sub: "Box English Out",    path: "/boxenglishtransfer" },
    ],
  },
  {
    perm: "खर्च",
    label: "खर्च", sub: "Expenses & Finance",
    Icon: FaRupeeSign, color: "#F59E0B",
    items: [
      { label: "खर्चा एंट्री",         sub: "Expense Entry",       path: "/expense" },
      { label: "पिछला खर्चा",          sub: "Previous Expense",    path: "/expensedate" },
      { label: "देसी कमीशन",          sub: "Desi Commission",     path: "/commissiondesi" },
      { label: "अंग्रेजी कमीशन",       sub: "English Commission",  path: "/commission" },
      { label: "स्टाफ वेतन",          sub: "Staff Salary",        path: "/employee" },
      { label: "नामे / जमा",          sub: "Debit / Credit",      path: "/creditdebit" },
      { label: "दर अंतर",             sub: "Rate Difference",     path: "/ratedifference" },
      { label: "प्रोडक्ट दर अंतर",    sub: "Product Rate Diff",   path: "/productratedifference" },
      { label: "फोनपे जमा",           sub: "PhonePe Deposit",     path: "/phonepaycredit" },
    ],
  },
  {
    perm: "रिपोर्ट",
    label: "रिपोर्ट", sub: "Reports",
    Icon: FaChartBar, color: "#8B5CF6",
    items: [
      { label: "बिकी रिपोर्ट",   sub: "Sales Report",    path: "/salesreport" },
      { label: "पर्चा रिपोर्ट",  sub: "Invoice Report",  path: "/report" },
      { label: "खर्च रिपोर्ट",   sub: "Expense Report",  path: "/expensereport" },
    ],
  },
];

/* ─── helpers ────────────────────────────────────────── */
const iconBox = (color) => ({
  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
  background: color + "1a", display: "flex",
  alignItems: "center", justifyContent: "center",
  color, fontSize: 13,
});

const hoverItem = (active, color) => ({
  display: "flex", alignItems: "center", gap: 10,
  padding: "10px 16px", cursor: "pointer",
  borderLeft: active ? `3px solid ${color}` : "3px solid transparent",
  background: active ? color + "18" : "transparent",
  transition: "background 0.15s",
});

/* ─── component ──────────────────────────────────────── */
export default function Sidebar({ isOpen, toggleSidebar }) {
  const { logout }   = useAuth();
  const navigate     = useNavigate();
  const location     = useLocation();
  const { shopId, groupId } = useShop();

  const [perms,    setPerms]    = useState([]);
  const [open,     setOpen]     = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  /* responsive */
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  /* permissions */
  useEffect(() => {
    axios
      .post("https://winenew.sipstocks.in/api/Liquor/GetGroupPermissions", { groupId, shopId })
      .then((res) => {
        if (res.data?.data) {
          const allowed = res.data.data.filter((m) => m.canView).map((m) => m.menuName.trim());
          setPerms(allowed);
          // auto-expand the section containing the current route
          const init = {};
          SECTIONS.forEach((s) => {
            if (s.items.some((i) => i.path === location.pathname)) init[s.perm] = true;
          });
          setOpen(init);
        }
      })
      .catch(console.error);
  }, []); // eslint-disable-line

  const toggleSection = (perm) => setOpen((prev) => ({ ...prev, [perm]: !prev[perm] }));

  const handleLogout = () => { logout(); navigate("/", { replace: true }); };

  const here = location.pathname;

  /* sidebar outer style */
  const sidebarStyle = {
    width: 240, minWidth: 240, background: BG,
    display: "flex", flexDirection: "column",
    ...(isMobile ? {
      position: "fixed", top: 0, left: 0, height: "100vh",
      zIndex: 1000,
      transform: isOpen ? "translateX(0)" : "translateX(-100%)",
      transition: "transform 0.28s ease",
      boxShadow: isOpen ? "4px 0 24px rgba(0,0,0,0.4)" : "none",
    } : {
      position: "sticky", top: 0, height: "100vh",
    }),
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && isOpen && (
        <div
          onClick={toggleSidebar}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 999 }}
        />
      )}

      <div style={sidebarStyle}>

        {/* ── Logo ── */}
        <div style={{
          padding: "18px 16px", display: "flex", alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}>
          <Link to="/home" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "linear-gradient(135deg,#6366F1,#8B5CF6)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
            }}>🍷</div>
            <span style={{ fontWeight: 800, fontSize: 15, color: "#fff", letterSpacing: 0.5 }}>WINESHOP</span>
          </Link>
          {isMobile && (
            <button onClick={toggleSidebar}
              style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 16, padding: 4 }}>
              <FaTimes />
            </button>
          )}
        </div>

        {/* ── Scrollable menu ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>

          {/* Section label */}
          <div style={{ padding: "10px 18px 6px", fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 1.2 }}>
            Main Menu
          </div>

          {/* Dashboard */}
          <Link to="/home" style={{ textDecoration: "none" }}
            onClick={() => isMobile && toggleSidebar()}>
            <div style={hoverItem(here === "/home", INDIGO)}
              onMouseEnter={(e) => { if (here !== "/home") e.currentTarget.style.background = HOVER_BG; }}
              onMouseLeave={(e) => { if (here !== "/home") e.currentTarget.style.background = "transparent"; }}>
              <div style={iconBox(INDIGO)}><FaHome /></div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: here === "/home" ? "#fff" : TEXT }}>Dashboard</div>
                <div style={{ fontSize: 10, color: MUTED }}>Overview</div>
              </div>
            </div>
          </Link>

          {/* Accordion sections */}
          {SECTIONS.map((s) => {
            if (!perms.includes(s.perm)) return null;
            const expanded   = !!open[s.perm];
            const childActive = s.items.some((i) => i.path === here);

            return (
              <div key={s.perm}>
                {/* Header */}
                <div
                  onClick={() => toggleSection(s.perm)}
                  style={{
                    ...hoverItem(childActive && !expanded, s.color),
                    justifyContent: "space-between",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = HOVER_BG}
                  onMouseLeave={(e) => e.currentTarget.style.background = (childActive && !expanded) ? s.color + "18" : "transparent"}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={iconBox(s.color)}><s.Icon /></div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: childActive ? "#fff" : TEXT }}>{s.label}</div>
                      <div style={{ fontSize: 10, color: MUTED }}>{s.sub}</div>
                    </div>
                  </div>
                  <FaChevronDown style={{
                    color: MUTED, fontSize: 10, flexShrink: 0,
                    transform: expanded ? "rotate(180deg)" : "rotate(0)",
                    transition: "transform 0.2s",
                  }} />
                </div>

                {/* Sub-items */}
                {expanded && (
                  <div style={{ background: BG2, paddingBottom: 4 }}>
                    {s.items.map((item) => {
                      const active = here === item.path;
                      return (
                        <Link key={item.path} to={item.path} style={{ textDecoration: "none" }}
                          onClick={() => isMobile && toggleSidebar()}>
                          <div
                            style={{
                              display: "flex", alignItems: "center", gap: 10,
                              padding: "8px 16px 8px 24px",
                              borderLeft: active ? `3px solid ${s.color}` : "3px solid transparent",
                              background: active ? s.color + "15" : "transparent",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = HOVER_BG; }}
                            onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                          >
                            <div style={{
                              width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                              background: active ? s.color : MUTED,
                            }} />
                            <div>
                              <div style={{ fontSize: 12, fontWeight: active ? 600 : 400, color: active ? "#fff" : TEXT }}>{item.label}</div>
                              <div style={{ fontSize: 10, color: MUTED }}>{item.sub}</div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Print Slip */}
          {perms.includes("प्रिंट पर्चा") && (
            <Link to="/printslip" style={{ textDecoration: "none" }}
              onClick={() => isMobile && toggleSidebar()}>
              <div style={hoverItem(here === "/printslip", "#EC4899")}
                onMouseEnter={(e) => { if (here !== "/printslip") e.currentTarget.style.background = HOVER_BG; }}
                onMouseLeave={(e) => { if (here !== "/printslip") e.currentTarget.style.background = "transparent"; }}>
                <div style={iconBox("#EC4899")}><FaPrint /></div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: here === "/printslip" ? "#fff" : TEXT }}>प्रिंट पर्चा</div>
                  <div style={{ fontSize: 10, color: MUTED }}>Print Invoice</div>
                </div>
              </div>
            </Link>
          )}

          {/* POS */}
          <Link to="/pos" style={{ textDecoration: "none" }}
            onClick={() => isMobile && toggleSidebar()}>
            <div style={hoverItem(here === "/pos", "#10B981")}
              onMouseEnter={(e) => { if (here !== "/pos") e.currentTarget.style.background = HOVER_BG; }}
              onMouseLeave={(e) => { if (here !== "/pos") e.currentTarget.style.background = "transparent"; }}>
              <div style={iconBox("#10B981")}><FaShoppingCart /></div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: here === "/pos" ? "#fff" : TEXT }}>POS</div>
                <div style={{ fontSize: 10, color: MUTED }}>Point of Sale</div>
              </div>
            </div>
          </Link>

        </div>

        {/* ── Logout ── */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <button
            onClick={handleLogout}
            style={{
              width: "100%", display: "flex", alignItems: "center",
              justifyContent: "center", gap: 8, padding: "11px 0",
              border: "none", borderRadius: 10, cursor: "pointer",
              background: "rgba(239,68,68,0.12)", color: "#F87171",
              fontSize: 13, fontWeight: 700, transition: "background 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(239,68,68,0.25)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(239,68,68,0.12)"}
          >
            <FaSignOutAlt /> Logout
          </button>
        </div>

      </div>
    </>
  );
}
